/**
 * BackToPortalButton — universal "return to your dashboard" pill that
 * appears at the bottom of every public resource / library / government
 * information page.
 *
 * Behaviour:
 *   • Logged-in landlord → "Back to landlord portal"  → /dashboard
 *   • Logged-in tenant   → "Back to tenant portal"    → /dashboard
 *   • Logged-in admin    → "Back to admin"            → /admin
 *   • Not logged in      → "Create your free account" → /signup
 *
 * Drop it in like `<BackToPortalButton />` at the end of any resource
 * page. It centres itself and adds its own vertical spacing so pages
 * don't need to.
 */
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, LayoutDashboard, Home, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function BackToPortalButton({ className = "" }) {
  // Call hook unconditionally per Rules of Hooks. AuthProvider wraps the
  // whole app so context should always be present; the `?.user` chain
  // handles the transient null before initial auth restore completes.
  const auth = useAuth();
  const user = auth?.user || null;
  const nav = useNavigate();

  const doLogout = async () => {
    try { await auth?.logout?.(); } catch { /* noop */ }
    nav("/", { replace: true });
  };

  let href = "/signup";
  let label = "Create your free account";
  let Icon = ArrowRight;
  let variant = "signup";

  if (user?.role === "landlord") {
    href = "/dashboard";
    label = "Back to landlord portal";
    Icon = LayoutDashboard;
    variant = "portal";
  } else if (user?.role === "tenant") {
    href = "/dashboard";
    label = "Back to tenant portal";
    Icon = Home;
    variant = "portal";
  } else if (user?.role === "admin") {
    href = "/admin";
    label = "Back to admin";
    Icon = ShieldCheck;
    variant = "portal";
  }

  return (
    <section
      data-testid="back-to-portal-section"
      className={`w-full py-10 sm:py-14 flex flex-col items-center justify-center gap-3 border-t border-slate-100 bg-slate-50 ${className}`}
    >
      <Link
        to={href}
        data-testid="back-to-portal-btn"
        data-portal-variant={variant}
        className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm sm:text-base font-extrabold shadow-lg shadow-emerald-500/30 transition-colors"
      >
        {variant === "portal" ? <ArrowLeft className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
        <span>{label}</span>
        {variant === "signup" && <ArrowRight className="w-4 h-4" />}
      </Link>
      {variant === "portal" && (
        <p className="text-xs text-slate-500">
          You&apos;re signed in as <span className="font-bold text-slate-700 capitalize">{user?.role}</span>.
          <Link to="/logout" onClick={(e) => { e.preventDefault(); doLogout(); }} className="ml-1.5 text-emerald-700 hover:underline font-semibold">Sign out</Link>
        </p>
      )}
      {variant === "signup" && (
        <p className="text-xs text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="text-emerald-700 hover:underline font-semibold">Sign in</Link>
        </p>
      )}
    </section>
  );
}
